import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getThunkAndSlice } from "@src/store/getThunkAndSlice";
import { RootState } from "@src/store/store";
import { ApiProvider } from "../ApiProvider";
import { PricingTabEnum, selectedInventoryItemIds } from "./selectionSlice";
import _ from "lodash";
import { getInventoryGroupKey } from "@src/components/utils/inventoryUtils";
import { collapseMarketplaceSync } from "@src/components/utils/syncStatus";
import { groupSeatNumbersWithRange } from "@src/components/utils/columnUtils";
import type { IGlobalInventoryRawItem } from "./queryGlobalInventorySlice";
import { mapGlobalInventoryBulkRow } from "./globalInventoryRow";

export interface EventInventoryRecord {
	Listing_ID: number;
	Event_ID: number;
	Event_Name?: string;
	Event_Headliner?: string;
	Event_Date?: string;
	Section: string;
	Row: string;
	Seat_Number: number;
	Seats: string;
	Quantity: number;
	Item_ID: number;
	Consignment: boolean;
	Company_Name: string;
	OnExchange: boolean;
	OnWeb: boolean;
	SH_eDelivery: boolean;
	Item_Cost: number;
	Item_Price: number;
	Face: number;
	Internal_Notes: string;
	External_Notes: string;
	Last_Price_Update: string;
	Days_Since_Last_Price_Update: number;
	StubHub_Listing_ID: bigint;
	StubHub_Event_ID: number;
	StubHub_Result: string;
	StubHub_Expiration_Date?: Date | null;
	StubHub_Splits?: number | null;
	StubHubInt_Listing_ID: bigint;
	StubHubInt_Event_ID: bigint;
	StubHubInt_Result: string;
	Child_Item_IDs: Array<number>;
	Is_AutoPriced: boolean;
	Is_No_Comparables: boolean;
	Is_At_Floor: boolean;
	Is_At_Ceiling: boolean;
	Is_Error: boolean;
	Ceiling_Price?: number | null;
	Floor_Price?: number | null;
	Allow_Price_Increase?: boolean | null;
	AutoPricer_User: string;
	Minutes_Per_Cycle?: number | null;
	On_Hold: boolean;
	Consignor_Hold: boolean;
	On_Hold_Comments: string;
	PDF_Attached: boolean;
	Barcode_Attached: boolean;
	Ticket_ID_Attached: boolean;
	Hide_Seats?: boolean | null;
	TicketsNow_Listing_ID: number;
	Vivid_Listing_ID: number;
	Vivid_Result: string;
	Vivid_Splits?: number | null;
	SeatGeek_Result: string;
	SeatGeek_Event_ID?: number | null;
	SeatGeek_Listing_ID?: number | null;
	TicketNetwork_Listing_ID: number;
	TicketNetwork_Result: string;
	TicketNetwork_Exchange_ID: number;
	TicketsNow_Result: string;
	InHand?: boolean | null;
	InHandDate?: Date | null;
	EDelivery?: boolean | null;
	Item_Status_ID: number;
	Export_Destination_ID_CSV: string;
	Tags: string | null;
	MasterRuleId?: number | null;
	MasterOrder?: number | null;
	MasterRuleName: string;
	Invoice_ID?: number | null;
	Invoice_Amount?: number | null;
	Invoice_Date?: Date | null;
	Exchange_Name: string;
	Payment_Amount?: number | null;
	Commission_Amount?: number | null;
	Buyers_Overs?: number | null;
	PO_ID?: number | null;
	PO_Date?: Date | null;
	Listing_Price?: number;
	Listing_Cost?: number;
	Confirmation_No: string;
	Purchaser_Name?: string;
	Account_Name?: string;
	Vendor?: string;
	Credit_Card?: string;
	Section_Locked?: boolean | null;
	Row_Locked?: boolean | null;
	Seat_Locked?: boolean | null;
	Cost_Locked?: boolean | null;
	Price_Locked?: boolean | null;
	Is_Scheduled_Pricing?: boolean | null;
	Shared_To: string;
	Completed_Date?: Date | null;
	Active_Tiers?: number | null;
	Is_Odd_Even: boolean;
	AXS_Listing_ID: number;
	FlashSaleEnabled?: boolean | null;
	FlashSaleDiscount?: number | null;
	IsListingSharedWithGT: boolean;
	Automation_Notes: string;
	Tickets_Available_Date?: Date | null;
	BarcodesAttachedNumber?: number;
	totalPDFsAttached?: number;
	CIMS_Listing_ID?: number;
	CIMS_Sync_Status?: number | null;
	CIMS_Sync_Message?: string | null;
	GT_Listing_ID?: string | null;
	GT_Sync_Status?: number | null;
	GT_Sync_Message?: string | null;
	VGG_Sync_Status?: number | null;
	VGG_Sync_Message?: string | null;
	TM_Sync_Status?: number | null;
	TM_Sync_Message?: string | null;
	SG_Sync_Status?: number | null;
	SG_Sync_Message?: string | null;
	GTX_Sync_Status?: number | null;
	GTX_Sync_Message?: string | null;
}
const initialState = new Array<EventInventoryRecord>();
export const {
	thunk: queryEventInventoryAll,
	slice: hiddenQueryEventInventoryAllSlice,
	extraReducers: hiddenExtraReducers,
} = getThunkAndSlice<
	{
		CompanyName?: string | null;
		EventId: number;
		ForceRefresh?: boolean | null;
	},
	{
		EventInventory: typeof initialState;
	},
	typeof initialState
>({
	initialState,
	path: "/api/Pricing/QueryEventInventoryAll",
	cacheId: "EventId",
	responsePath: "EventInventory",
	method: "post",
});

export const QUERY_EVENT_INVENTORY_PATH_KEY = "_api_Pricing_QueryEventInventoryAll";

export const invalidateEventInventoryCache = (eventIds: Array<number>) => {
	eventIds.forEach((id) => ApiProvider.default.removeCache(QUERY_EVENT_INVENTORY_PATH_KEY, String(id)));
};

export const invalidateAllEventInventoryCache = () => ApiProvider.default.removeCachePath(QUERY_EVENT_INVENTORY_PATH_KEY);

export const getInventoryCacheEventId = (state: RootState): number | undefined =>
	state.pricing.selection.pricingTab === PricingTabEnum.GlobalInventory ? undefined : state.pricing.selection.event?.Event_ID;

export const queryEventInventoryAllSlice = createSlice({
	name: hiddenQueryEventInventoryAllSlice.name,
	initialState: hiddenQueryEventInventoryAllSlice.getInitialState(),
	reducers: {
		updateEventInventoryItemsTags: (
			state,
			action: PayloadAction<{ itemIds: Array<number>; tags: Array<string>; eventId?: number }>,
		) => {
			const { itemIds, tags, eventId } = action.payload;
			state.data = state.data.map((x) => {
				if (itemIds.includes(x.Listing_ID)) {
					const Tags = tags.length > 0 ? tags.join("|") : null;
					return {
						...x,
						Tags,
					};
				}
				return x;
			});
			if (eventId) {
				ApiProvider.default.setCache(QUERY_EVENT_INVENTORY_PATH_KEY, String(eventId), state.data);
			}
			state.version++;
		},
		removeItemTag: (state, action: PayloadAction<{ itemIds: Array<number>; tag: string; eventId?: number }>) => {
			const { itemIds, tag, eventId } = action.payload;
			const newState = state.data.map((x) => {
				if (itemIds.includes(x.Item_ID)) {
					const newTags = x.Tags?.split("|").filter((i) => i !== tag);
					const Tags = newTags && newTags.length > 0 ? newTags.join("|") : null;
					return {
						...x,
						Tags,
					};
				}
				return x;
			});
			if (eventId) {
				ApiProvider.default.setCache(QUERY_EVENT_INVENTORY_PATH_KEY, String(eventId), newState);
			}

			state.data = newState;
			state.version++;
		},
		updateInventoryAll: (state, action: PayloadAction<{ data: Array<EventInventoryRecord>; eventId?: number }>) => {
			state.data = action.payload.data;
			state.version++;
			const { data, eventId } = action.payload;
			if (eventId) {
				ApiProvider.default.setCache(QUERY_EVENT_INVENTORY_PATH_KEY, String(eventId), data);
			}
		},
		reset: (state) => {
			state.data = [];
		},
	},
	extraReducers: hiddenExtraReducers,
});

const _selectEventInventoryData = (state: RootState) => state.pricing.eventInventoryAll.data;
const _selectEventInventoryLoading = (state: RootState) => state.pricing.eventInventoryAll.loading;
const _selectEventInventoryError = (state: RootState) =>
	state.pricing.eventInventoryAll.error ?? "No Event Inventory Data Available";

export const selectEventInventoryData = createSelector(
	[_selectEventInventoryData, _selectEventInventoryLoading, _selectEventInventoryError],
	(eventInventoryData, eventInventoryLoading, eventInventoryError) => ({
		eventInventoryData,
		eventInventoryLoading,
		eventInventoryError,
	}),
);

export const selectAutoPricerActiveItem = createSelector(
	[_selectEventInventoryData, selectedInventoryItemIds],
	(inventoyData, selectedInventoryIds) => {
		const SEND_ALL_ITEM_IDS = false;

		const selectedItems = inventoyData.filter((record) => selectedInventoryIds.includes(record.Item_ID));
		const activeListing = selectedItems?.[0];

		const activeListingId = activeListing ? [activeListing?.Item_ID] : [];

		const allItemIds = inventoyData
			.filter((x) => x.Listing_ID === activeListing?.Listing_ID && !x.Invoice_ID)
			.map((x) => x.Item_ID);

		const itemIds = SEND_ALL_ITEM_IDS ? allItemIds : activeListingId;

		return { activeListing, itemIds };
	},
);

const _selectInventorySelectedIds = (state: RootState) => state.pricing.selection.inventoryItemIds;
const _selectIsGlobalInventoryTab = (state: RootState) => state.pricing.selection.pricingTab === PricingTabEnum.GlobalInventory;
const _selectGlobalInventoryData = (state: RootState) => state.pricing.globalInventory.data;

const _selectGlobalSelectedInventoryItems = createSelector(
	[_selectGlobalInventoryData, _selectInventorySelectedIds],
	(globalInventory, selectedIds) => {
		const selected = new Set(selectedIds);
		return _.sortBy(
			(globalInventory as Array<IGlobalInventoryRawItem>).filter((x) => selected.has(x.Listing_ID) && !x.Invoice_ID),
			"Listing_ID",
		).map(mapGlobalInventoryBulkRow);
	},
);

const _selectEventSelectedInventoryItems = createSelector(
	[_selectEventInventoryData, _selectInventorySelectedIds],
	(eventInventory, selectedInvenrotyIds) => {
		const sortedData = _.sortBy(eventInventory, "Listing_ID");
		const groupedData = _.groupBy(sortedData, getInventoryGroupKey);

		const rowsData = _.map(groupedData, (value) => {
			const seatNumbers = value.map((x) => x.Seat_Number);
			return {
				...value[0],
				Seats: groupSeatNumbersWithRange(seatNumbers),
				Quantity: value.length,
				Item_Cost: value.reduce((acc, curr) => acc + curr.Item_Cost, 0),
				...collapseMarketplaceSync(value),
			};
		}).filter((x) => {
			return !x.Invoice_ID;
		});

		const filteredItems = _.filter(rowsData, (x) => selectedInvenrotyIds.includes(x.Item_ID));

		return filteredItems;
	},
);

export const selectSelectedInventoryItems = (state: RootState) =>
	_selectIsGlobalInventoryTab(state) ? _selectGlobalSelectedInventoryItems(state) : _selectEventSelectedInventoryItems(state);
